import type { Dispatch, MutableRefObject, SetStateAction } from 'react'
import {
  CALENDLY_SYSTEM_MESSAGES,
  DEFAULT_LANGUAGE,
  type LanguageCode,
} from '../config/language'
import { typingSound } from '../utils/typing-sound'
import type { ContactInfo, Message, ScheduledMeeting } from '../types'

// ============================================
// runScheduleCallback — schedule_callback tool handler
// ============================================
// Lifted from VoiceBotPanel.handleFunctionCall as part of Wave 3b.
//
// Verbal-callback flow (no Calendly): when the bot collects enough
// information to register a callback, this handler:
//   - In `during_conversation` contact-form mode without contact info,
//     opens the contact popup and waits for the user to fill it.
//   - Builds a ScheduledMeeting from args + contact info + transcript
//     name fallback, then commits it to state and refs.
//   - Marks the booking as confirmed and sets the
//     pending-end-after-speaking flag so the session closes after the
//     bot says goodbye.
//
// Behavior is intentionally preserved 1:1 with the original inline
// handler.

export interface RunScheduleCallbackOptions {
  args: Record<string, unknown>
  transcript: Message[]
  configRef: MutableRefObject<{ contactFormTiming?: string }>
  contactInfoRef: MutableRefObject<ContactInfo | null>
  pendingCustomerNameRef: MutableRefObject<string | null>
  dataChannelRef: MutableRefObject<RTCDataChannel | null>
  selectedLanguageRef: MutableRefObject<LanguageCode>
  scheduledMeetingRef: MutableRefObject<ScheduledMeeting | null>
  setScheduledMeeting: Dispatch<SetStateAction<ScheduledMeeting | null>>
  setBookingConfirmed: (next: boolean) => void
  setPendingEndAfterSpeaking: (next: boolean) => void
  waitForDeferredContactInfo: () => Promise<ContactInfo | null>
  formatTime: (dateStr: string, lang: LanguageCode) => string
  extractCustomerNameFromTranscript: (
    transcript: { role: string; content: string }[]
  ) => string | undefined
}

export const runScheduleCallback = async (
  opts: RunScheduleCallbackOptions
): Promise<string> => {
  const {
    args,
    transcript,
    configRef,
    contactInfoRef,
    pendingCustomerNameRef,
    dataChannelRef,
    selectedLanguageRef,
    scheduledMeetingRef,
    setScheduledMeeting,
    setBookingConfirmed,
    setPendingEndAfterSpeaking,
    waitForDeferredContactInfo,
    formatTime,
    extractCustomerNameFromTranscript,
  } = opts

  console.log('📞 schedule_callback called with args:', args)

  const lang: LanguageCode = selectedLanguageRef.current || DEFAULT_LANGUAGE
  const langMessages =
    CALENDLY_SYSTEM_MESSAGES[lang] || CALENDLY_SYSTEM_MESSAGES[DEFAULT_LANGUAGE]

  // In during_conversation mode without contact info, show popup to collect details
  const isDuringConvMode =
    (configRef.current.contactFormTiming || 'none') === 'during_conversation'
  let currentContactForCallback = contactInfoRef.current

  if (!currentContactForCallback && isDuringConvMode) {
    console.log(
      '📞 During-conversation mode: showing contact popup before callback registration...'
    )
    pendingCustomerNameRef.current = (args.customerName as string) || null

    // Stop typing sound while user fills form
    typingSound.stop()

    // Instruct bot to tell user about the popup — localized so the bot
    // does not switch to Hebrew when the user picked another language.
    if (dataChannelRef.current?.readyState === 'open') {
      dataChannelRef.current.send(
        JSON.stringify({
          type: 'conversation.item.create',
          item: {
            type: 'message',
            role: 'user',
            content: [
              {
                type: 'input_text',
                text: langMessages.deferredContactPromptCallback,
              },
            ],
          },
        })
      )
      dataChannelRef.current.send(JSON.stringify({ type: 'response.create' }))
    }

    // Wait for user to fill the popup
    currentContactForCallback = await waitForDeferredContactInfo()

    if (!currentContactForCallback?.email && !currentContactForCallback?.phone) {
      return 'CALLBACK CANCELLED: Customer closed the contact form without filling it. Ask if they changed their mind or want to continue the conversation.'
    }
  }

  // Build datetime from date + time (optional — Calendly-off mode skips these)
  const cbDate = (args.callbackDate as string) || ''
  const cbTime = (args.callbackTime as string) || ''
  const cbDatetime = cbDate && cbTime ? `${cbDate}T${cbTime}:00` : ''
  const cbFormatted = cbDatetime
    ? formatTime(cbDatetime, selectedLanguageRef.current)
    : ''

  // Resolve customer name from args, contact info, or transcript
  const cbCustomerName =
    (args.customerName as string) ||
    currentContactForCallback?.name ||
    contactInfoRef.current?.name ||
    extractCustomerNameFromTranscript(transcript) ||
    ''

  const cbMeetingType = (args.meetingType as string) || undefined
  const cbNotes =
    [
      (args.notes as string) || '',
      cbMeetingType
        ? `סוג פגישה: ${cbMeetingType === 'physical' ? 'פיזית' : 'אונליין'}`
        : '',
    ]
      .filter(Boolean)
      .join('. ') || undefined

  const callbackMeeting: ScheduledMeeting = {
    datetime: cbDatetime,
    formattedTime: cbFormatted,
    customerName: cbCustomerName,
    customerEmail:
      currentContactForCallback?.email || contactInfoRef.current?.email || '',
    customerPhone:
      (args.customer_phone as string) ||
      currentContactForCallback?.phone ||
      contactInfoRef.current?.phone ||
      '',
    source: 'verbal',
    notes: cbNotes,
  }

  setScheduledMeeting(callbackMeeting)
  scheduledMeetingRef.current = callbackMeeting
  // Mark as confirmed immediately — no Calendly confirmation step needed.
  // Wave 6e5: setter writes bookingConfirmedRef synchronously.
  setBookingConfirmed(true)

  console.log('📞 Verbal callback registered:', callbackMeeting)

  // Auto-close: bot says goodbye, then session ends when audio finishes
  setPendingEndAfterSpeaking(true)

  return cbFormatted
    ? langMessages.callbackRegisteredFormatted(cbFormatted)
    : langMessages.callbackRegisteredGeneric
}
